/**
 * Hook for fetching and managing the memo records — notes or tasks, per
 * `recordType` — associated with an entity.
 *
 * Split across sibling modules: useMemoRecords.queries.ts (association
 * resolution + the store-backed list query), useMemoRecords.updateMutation.ts
 * (update), useMemoRecords.stagePrivacyMutations.ts
 * (stage-change/privacy-toggle/delete). This file composes them into the
 * single public hook below, whose return shape covers reading, updating,
 * stage changes, privacy toggling, and deletion — but not creation. Note
 * creation lives in useMemoCreate.tsx.
 */

import type { Membership } from 'api-client';
import { useCallback, useMemo } from 'react';
import { toast } from 'sonner';

import { logger } from '@/lib/logger';

import type { UseMemoRecordsOptions } from './notes.types';
import {
  getMemoAssociationNames,
  type MemoEntityType,
  type MemoPrivacy,
} from './notes.utils';
import { useMemoRecordsQueries } from './useMemoRecords.queries';
import { useMemoRecordsStagePrivacyMutations } from './useMemoRecords.stagePrivacyMutations';
import { useUpdateRecordMutation } from './useMemoRecords.updateMutation';

export type { Note } from './notes.types';
export { isOwnerOrCreator } from './notes.utils';

/**
 * Hook to fetch the memo records (notes by default, tasks with
 * `recordType: 'ahoy_task'`) associated with an entity.
 *
 * @example
 * const { records, isLoading, deleteRecord } = useMemoRecords({
 *   orgId: 1,
 *   entityId: 'abc-123',
 *   entityTypeName: 'ahoy_contact'
 * });
 */
export function useMemoRecords({
  orgId,
  entityId,
  entityTypeName,
  users,
  recordType = 'ahoy_note',
}: UseMemoRecordsOptions) {
  // The association family every read and write below runs under — the note
  // family by default, the task family for the Tasks section and for a task
  // opened in the compose panel.
  const names = getMemoAssociationNames(recordType);

  // Create set of valid user entity IDs for mention validation
  const validUserEntityIds = useMemo(
    () => new Set(users.map(u => u.entityId)),
    [users]
  );

  const {
    records,
    isLoading,
    error,
    refetch,
    ownerAssociationTypeName,
    notesListKey,
    invalidateNoteObjectQuery,
  } = useMemoRecordsQueries({
    orgId,
    entityId,
    entityTypeName,
    recordType,
    names,
  });

  const updateRecordMutation = useUpdateRecordMutation({
    orgId,
    recordType,
    names,
    notesListKey,
    ownerAssociationTypeName,
    validUserEntityIds,
    invalidateNoteObjectQuery,
  });

  const {
    updateRecordStageAsync,
    togglePrivacyMutation,
    deleteRecordMutation,
  } = useMemoRecordsStagePrivacyMutations({
    orgId,
    recordType,
    names,
    notesListKey,
    invalidateNoteObjectQuery,
  });

  return {
    records,
    isLoading,
    error,
    refetch,
    updateRecord: updateRecordMutation.mutate,
    updateRecordAsync: updateRecordMutation.mutateAsync,
    isUpdating: updateRecordMutation.isPending,
    updateError: updateRecordMutation.error,
    // Isolated stage-change path — pending/error deliberately NOT exposed so
    // they can never be wired into a NoteCard's edit lifecycle.
    updateRecordStageAsync,
    togglePrivacy: togglePrivacyMutation.mutate,
    togglePrivacyAsync: togglePrivacyMutation.mutateAsync,
    isTogglingPrivacy: togglePrivacyMutation.isPending,
    togglePrivacyError: togglePrivacyMutation.error,
    deleteRecord: deleteRecordMutation.mutateAsync,
    isDeleting: deleteRecordMutation.isPending,
    deleteError: deleteRecordMutation.error,
  };
}

export interface UseMemoPrivacyToggleOptions {
  orgId: number;
  /** Which memo family the record belongs to — picks the toast's noun. */
  recordType: MemoEntityType;
  /** Org roster, for the mention validation useMemoRecords runs internally. */
  users: Membership[];
}

export interface MemoPrivacyToggle {
  /**
   * Flip one memo's visibility, reporting success and failure to the user.
   * Resolves `true` when the change persisted and `false` when the backend
   * rejected it (toast + log already emitted). Callers that close an editor on
   * save MUST branch on this so a failed visibility save keeps the edit open
   * and retryable — a `false` here must not read to the user as a clean save.
   */
  togglePrivacy: (
    memoEntityId: string,
    target: MemoPrivacy
  ) => Promise<boolean>;
  isTogglingPrivacy: boolean;
}

/**
 * The record slideouts' visibility control, in one place.
 *
 * Privacy lives behind a dedicated POST endpoint (see
 * useMemoRecords.stagePrivacyMutations' togglePrivacyMutation) rather than a
 * property write, and every caller owes the user the same three things around
 * it: a success toast naming what changed, an error toast when the backend
 * rejects it (e.g. the org's entity_id isn't provisioned yet, which blocks the
 * visible_to_org association), and a structured log so the rejection is
 * diagnosable. Bundling them here is what keeps "I clicked Shared but it
 * stayed Private, with no feedback" from coming back one surface at a time.
 *
 * Only the mutation is used — the list query is skipped (`entityId:
 * undefined`), so this pulls no records.
 */
export function useMemoPrivacyToggle({
  orgId,
  recordType,
  users,
}: UseMemoPrivacyToggleOptions): MemoPrivacyToggle {
  const { togglePrivacyAsync, isTogglingPrivacy } = useMemoRecords({
    orgId,
    entityId: undefined,
    entityTypeName: '',
    users,
    recordType,
  });

  const noun = recordType === 'ahoy_task' ? 'Task' : 'Note';

  const togglePrivacy = useCallback(
    async (memoEntityId: string, target: MemoPrivacy): Promise<boolean> => {
      try {
        await togglePrivacyAsync({
          noteEntityId: memoEntityId,
          targetPrivacy: target,
        });
        toast.success(
          target === 'shared'
            ? `${noun} shared with the org`
            : `${noun} made private`
        );
        return true;
      } catch (err: unknown) {
        toast.error("We couldn't change visibility. Try again.");
        logger.error(
          'useMemoPrivacyToggle: togglePrivacy failed',
          err instanceof Error ? err : new Error(String(err)),
          {
            org_id: orgId,
            entity_id: memoEntityId,
            target_privacy: target,
          }
        );
        // Report failure so callers keep the editor open and retryable instead
        // of closing on a swallowed rejection. Toast + log already emitted.
        return false;
      }
    },
    [togglePrivacyAsync, noun, orgId]
  );

  return useMemo(
    () => ({ togglePrivacy, isTogglingPrivacy }),
    [togglePrivacy, isTogglingPrivacy]
  );
}
